// Preview helpers and sample data matching the prototype exactly

// Gradient colors for placeholders
export const GRADIENTS = [
  '#3ecf8e,#14b8a6', // green-teal
  '#f59e0b,#ef4444', // orange-red
  '#8b5cf6,#ec4899', // purple-pink
  '#06b6d4,#3b82f6', // cyan-blue
  '#10b981,#059669', // emerald
];

// Sample data
export const SAMPLE_LINKS = [
  'Getting Started Guide',
  'Best Practices for SEO',
  'Advanced Configuration Tips',
  'Troubleshooting Issues',
  'Performance Optimization',
  'Integration Guide',
  'API Reference',
  'Security Best Practices',
];

export const SAMPLE_PRODUCTS = [
  'Product A',
  'Product B',
  'Product C',
  'Product D',
  'Product E',
  'Product F',
  'Product G',
  'Product H',
];

export const SAMPLE_ARTICLES = [
  { cat: 'MARKETING', title: '10 Tips for Better Content', date: 'Jan 15', time: '5' },
  { cat: 'SEO', title: 'Advanced SEO Strategies', date: 'Jan 10', time: '8' },
  { cat: 'GROWTH', title: 'Scaling Your Business', date: 'Jan 5', time: '6' },
  { cat: 'TECH', title: 'Web Performance Guide', date: 'Jan 3', time: '7' },
];

export const COLUMN_HEADINGS = [
  'Related Products',
  'Related Blogs',
  'Related Tags',
  'Related Categories',
  'More Links',
];

export const SIDEBAR_TITLES = [
  'Quick Guide On Spend',
  'Expert Insights On SaaS',
  'Best Practices Guide',
  'Getting Started Tutorial',
];

// Helper functions matching the prototype
export function getGradient(index: number): string {
  return `linear-gradient(135deg, ${GRADIENTS[index % GRADIENTS.length]})`;
}

export function getPadding(size: string): string {
  const sizes: Record<string, string> = {
    none: '0',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
  };
  return sizes[size] || sizes.md;
}

export function getSpacing(size: string): string {
  const sizes: Record<string, string> = {
    tight: '0.35rem',
    normal: '0.5rem',
    relaxed: '0.75rem',
  };
  return sizes[size] || sizes.normal;
}

export function getRadius(size: string): string {
  const sizes: Record<string, string> = {
    none: '0',
    sm: '4px',
    md: '6px',
    lg: '12px',
  };
  return sizes[size] || sizes.md;
}

export function getGap(size: string): string {
  const sizes: Record<string, string> = {
    none: '0',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
  };
  return sizes[size] || sizes.md;
}

// Get aspect ratio height based on ratio and card size
export function getAspectHeight(ratio: string, size = 'medium'): number {
  const sizeMultipliers: Record<string, number> = {
    small: 0.7,
    medium: 1,
    large: 1.3,
  };
  const multiplier = sizeMultipliers[size] || 1;

  const baseHeights: Record<string, number> = {
    '1:1': 160,
    '16:9': 135,
    '4:3': 150,
    '3:2': 140,
    portrait: 200,
  };

  return Math.round((baseHeights[ratio] || 135) * multiplier);
}

// Get card width based on size
export function getCardWidth(size = 'medium'): number {
  const widths: Record<string, number> = {
    small: 180,
    medium: 240,
    large: 320,
  };
  return widths[size] || 240;
}

// Get carousel slide width based on size
export function getCarouselSlideWidth(size = 'medium'): number {
  const widths: Record<string, number> = {
    small: 160,
    medium: 220,
    large: 280,
  };
  return widths[size] || 220;
}

// Get font size based on card size
export function getFontSize(size = 'medium'): string {
  const sizes: Record<string, string> = {
    small: '0.75rem',
    medium: '0.85rem',
    large: '1rem',
  };
  return sizes[size] || '0.85rem';
}

// Get image size for sidebar cards
export function getImageSize(size: string): string {
  const sizes: Record<string, string> = {
    small: '50px',
    medium: '70px',
    thumbnail: '40px',
  };
  return sizes[size] || '55px';
}

// Get link decoration style
export function getLinkDecoration(style: string): React.CSSProperties {
  if (style === 'underline') {
    return { textDecoration: 'underline' };
  }
  if (style === 'none' || style === 'hover-underline') {
    return { textDecoration: 'none' };
  }
  return { textDecoration: 'none' };
}

// Get container background style
export function getContainerBackground(style: string): React.CSSProperties {
  if (style === 'subtle') {
    return { background: '#f8fafc' };
  }
  if (style === 'card') {
    return { background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
  }
  return {};
}

// Get card shadow style
export function getCardShadow(style: string): React.CSSProperties {
  if (style === 'elevated') {
    return { boxShadow: '0 4px 12px rgba(0,0,0,0.15)' };
  }
  if (style === 'bordered') {
    return { border: '1px solid #e2e8f0' };
  }
  return {};
}

// Get sidebar container style
export function getSidebarContainerStyle(style: string): React.CSSProperties {
  if (style === 'bordered') {
    return { border: '1px solid #e2e8f0' };
  }
  if (style === 'shadowed') {
    return { boxShadow: '0 2px 8px rgba(0,0,0,0.1)' };
  }
  return {};
}

// Get heading style for sidebar
export function getHeadingStyle(style: string): React.CSSProperties {
  if (style === 'uppercase') {
    return { textTransform: 'uppercase' as const, letterSpacing: '0.5px', fontSize: '0.75rem' };
  }
  if (style === 'underlined') {
    return { borderBottom: '2px solid #3ecf8e', paddingBottom: '0.25rem' };
  }
  return { fontWeight: 700 };
}

// Get inline callout style
export function getInlineCalloutStyle(style: string): React.CSSProperties {
  if (style === 'bordered') {
    return { border: '1px solid #3ecf8e', borderRadius: '8px' };
  }
  if (style === 'background') {
    return { background: 'rgba(62,207,142,0.1)', borderRadius: '8px' };
  }
  // left-border (default)
  return {
    borderLeft: '4px solid #3ecf8e',
    borderRadius: '0 8px 8px 0',
    background: 'rgba(62,207,142,0.1)',
  };
}

// Get width style
export function getWidthStyle(width: string): string {
  const widths: Record<string, string> = {
    full: '100%',
    half: '50%',
    third: '33%',
  };
  return widths[width] || '100%';
}

// Get float style
export function getFloatStyle(position: string): React.CSSProperties {
  if (position === 'left') {
    return { float: 'left' as const, marginRight: '1rem', marginBottom: '0.5rem' };
  }
  if (position === 'right') {
    return { float: 'right' as const, marginLeft: '1rem', marginBottom: '0.5rem' };
  }
  return {};
}

// Get contextual box border style
export function getContextualBorderStyle(style: string): React.CSSProperties {
  if (style === 'solid') {
    return { border: '2px solid #3ecf8e' };
  }
  if (style === 'dashed') {
    return { border: '2px dashed #3ecf8e' };
  }
  return { background: '#f8fafc' };
}

// Get card background color
export function getCardBackground(bg: string): string {
  const backgrounds: Record<string, string> = {
    gray: '#f8fafc',
    transparent: 'transparent',
    white: 'white',
  };
  return backgrounds[bg] || 'white';
}

// Get divider style for columns
export function getColumnDividerStyle(divider: string, index: number): React.CSSProperties {
  if (index === 0) return {};

  if (divider === 'line') {
    return { borderLeft: '1px solid #e2e8f0', paddingLeft: '0.75rem' };
  }
  if (divider === 'shadow') {
    return { boxShadow: '-3px 0 6px rgba(0,0,0,0.08)', paddingLeft: '0.75rem' };
  }
  return {};
}

// Generate sample links
export function generateLinks(count: number, linkTexts: string[] = SAMPLE_LINKS): string[] {
  return linkTexts.slice(0, count);
}

// Colors
export const COLORS = {
  primary: '#3ecf8e',
  primaryDark: '#2eb67d',
  navy: '#1e3a5f',
  textMuted: '#64748b',
  border: '#e2e8f0',
  background: '#f8fafc',
  white: '#ffffff',
};
